import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { SearchBox, GeneralSelect, TimeSelectorRow, GeneralButton } from 'sharedComponents';
import { LoadingIndicator } from 'sharedComponents/PowertrainLoadingIndicator';
import ConnectionHistoryChartComponent from './ConnectionHistoryChart/ConnectionHistoryChartComponent';
import { apiCallStatus } from 'helpers/constants';
import {
    SORT_OPTIONS_VALUES,
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
    MAX_DATE_RANGE_DAYS,
} from './constants';
import { mapResponseToAssets } from './helpers';
import './style.scss';
import { IconNextPage, IconPreviousPage } from 'svgIcons/MotionPortalIcons';

const MAX_SEARCH_LENGTH = 50;


const buildQueryKey = ({ gatewayId, pageSize, keyword, sortBy, startTime, endTime }) =>
    JSON.stringify({ gatewayId, pageSize, keyword: keyword ?? '', sortBy, startTime: String(startTime), endTime: String(endTime) });

const ConnectedHistoryComponent = ({ gatewayID, model, actions, onMount }) => {
    const { t: translate } = useTranslation();

    const [searchValue, setSearchValue] = useState('');
    const [sortOption, setSortOption] = useState({ value: SORT_OPTIONS_VALUES.DISCONNECTED_FIRST });
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [currentPage, setCurrentPage] = useState(1);
    const rootRef = useRef();
    const chartWrapperRef = useRef();
    const paginationRef = useRef();
    const [chartMaxHeight, setChartMaxHeight] = useState(undefined);

    const modelRef = useRef(model);
    modelRef.current = model;
    const isMountFetchRef = useRef(true);
    useEffect(() => {
        onMount?.();
    }, []);

    const sortOptions = useMemo(() => [
        { value: SORT_OPTIONS_VALUES.DISCONNECTED_FIRST, label: translate('ABB.Powertrain.Frontend.gatewayManagementDisconnectedFirst') },
        { value: SORT_OPTIONS_VALUES.CONNECTED_FIRST, label: translate('ABB.Powertrain.Frontend.gatewayManagementConnectedFirst') },
    ], [translate]);

    const pageSizeOptions = useMemo(() =>
        PAGE_SIZE_OPTIONS.map((size) => ({ value: size, label: String(size) })),
        []
    );

    const recalcHeight = useCallback(() => {
        if (!chartWrapperRef.current) { return; }
        const rect = chartWrapperRef.current.getBoundingClientRect();
        if (!rect.top && !rect.height) { return; }
        const panelEl = rootRef.current
            ? rootRef.current.closest('.gateway-management__right-panel') ||
            rootRef.current.closest('.gateway-management__right-pane')
            : null;
        const panelPaddingBottom = panelEl
            ? Number.parseFloat(getComputedStyle(panelEl).paddingBottom) || 0
            : 0;
        const panelInnerBottom = panelEl
            ? panelEl.getBoundingClientRect().bottom - panelPaddingBottom
            : window.innerHeight;

        const paginationH = paginationRef.current ? paginationRef.current.offsetHeight : 0;
        const bottomGap = paginationH > 0 ? paginationH + 8 : 8;
        setChartMaxHeight(Math.max(panelInnerBottom - rect.top - bottomGap, 80));
    }, []);

    // header wrapping, viewport resize.
    useEffect(() => {
        const frame = requestAnimationFrame(recalcHeight);
        const ro = new ResizeObserver(recalcHeight);
        if (rootRef.current) {
            ro.observe(rootRef.current);
            if (rootRef.current.parentElement) {
                ro.observe(rootRef.current.parentElement);
            }
        }
        window.addEventListener('resize', recalcHeight);
        return () => {
            cancelAnimationFrame(frame);
            ro.disconnect();
            window.removeEventListener('resize', recalcHeight);
        };
    }, [recalcHeight]);

    const fetchHistory = useCallback((nextCursor, prevCursor) => {
        if (!fromDate || !toDate) { return; }
        actions.getConnectedHistory({
            gatewayId: gatewayID,
            pageSize,
            keyword: searchValue,
            sortBy: sortOption?.value ?? SORT_OPTIONS_VALUES.DISCONNECTED_FIRST,
            startTime: fromDate,
            endTime: toDate,
            nextCursor: nextCursor ?? '',
            prevCursor: prevCursor ?? '',
        });
    }, [gatewayID, pageSize, searchValue, sortOption?.value, fromDate, toDate, actions]);

    // Fetch from scratch whenever filters change.
    useEffect(() => {
        if (!fromDate || !toDate) { return; }

        const currentParams = {
            gatewayId: gatewayID,
            pageSize,
            keyword: searchValue,
            sortBy: sortOption?.value ?? SORT_OPTIONS_VALUES.DISCONNECTED_FIRST,
            startTime: fromDate,
            endTime: toDate,
            nextCursor: '',
            prevCursor: '',
        };

        const cached = modelRef.current;
        const isMountFetch = isMountFetchRef?.current;
        isMountFetchRef.current = false;
        if (
            !isMountFetch &&
            cached?.data &&
            cached?.lastQueryParams &&
            !cached.data.hasPreviousPage &&
            buildQueryKey(currentParams) === buildQueryKey(cached.lastQueryParams)
        ) {
            return;
        }

        actions.clearConnectedHistory();
        setCurrentPage(1);
        fetchHistory('', '');
    }, [gatewayID, pageSize, searchValue, sortOption?.value, fromDate, toDate, actions]);

    const handleSelectDate = (from, to) => {
        setFromDate(from);
        setToDate(to);
    };

    const handleSortChange = (selected) => { setSortOption(selected); };

    const handlePageSizeChange = (selected) => { setPageSize(selected?.value); };

    const responseData = model?.data;
    const assets = useMemo(() => mapResponseToAssets(responseData?.assetConnectionDetails), [responseData]);
    const hasNextPage = responseData?.hasNextPage;
    const hasPreviousPage = responseData?.hasPreviousPage;
    const nextCursor = responseData?.nextCursor;
    const prevCursor = responseData?.prevCursor;
    const totalCount = responseData?.totalCount ?? null;
    const showPagination = hasNextPage || hasPreviousPage || totalCount > 10;
    const isLoading = model?.loadingStatus === apiCallStatus.LOADING;

    useEffect(() => { requestAnimationFrame(recalcHeight); }, [showPagination]);

    // Page range: e.g. "1-10 of 100" or "1-10" when total unknown
    const pageRangeStart = (currentPage - 1) * pageSize + 1;
    const pageRangeEnd = (currentPage - 1) * pageSize + (assets?.length || 0);
    const pageRangeLabel = totalCount === null
        ? `${pageRangeStart}\u2013${pageRangeEnd}`
        : `${pageRangeStart}\u2013${pageRangeEnd} ${translate('ABB.Powertrain.Frontend.gatewayManagementOfLabel')} ${totalCount}`;

    const selectedSort = sortOptions.find((o) => o.value === sortOption?.value) || sortOptions[0];
    const selectedPageSize = pageSizeOptions.find((o) => o.value === pageSize) || pageSizeOptions[0];

    return (
        <div className='connection-history' ref={rootRef}>
            {/* Header bar */}
            <div className='connection-history__header'>
                <div className='connection-history__search'>
                    <SearchBox
                        className='connection-history__search-box'
                        placeholder={translate('ABB.Powertrain.Frontend.gatewayManagementSearchAssetNameOrSN')}
                        value={searchValue}
                        onChange={setSearchValue}
                        maxLength={MAX_SEARCH_LENGTH}
                        id='connection-history-search'
                    />
                </div>
                <div className='connection-history__filters'>
                    <div className='connection-history__sort'>
                        <span className='connection-history__sort-label'>
                            {translate('ABB.Powertrain.Frontend.gatewayManagementSortingLabel')}:
                        </span>
                        <GeneralSelect
                            className='connection-history__sort-select'
                            options={sortOptions}
                            value={selectedSort}
                            onChange={handleSortChange}
                            isSearchable={false}
                            inputId='connection-history-sort'
                            name='connection-history-sort'
                        />
                    </div>
                    <div className='connection-history__date-picker'>
                        <TimeSelectorRow
                            fromDate={fromDate}
                            toDate={toDate}
                            handleSelectDate={handleSelectDate}
                            label={''}
                            maxDaysRange={MAX_DATE_RANGE_DAYS}
                            maxDaysBack={MAX_DATE_RANGE_DAYS}
                            preSelectedDays={14}
                            numberOfMonths={2}
                            showNumberOfDays={true}
                            footerIntervalText={translate('ABB.Powertrain.Frontend.datePickerMax90DaysNote')}
                        />
                    </div>
                </div>
            </div>

            {/* Chart area */}
            <div
                className='connection-history__chart-wrapper'
                ref={chartWrapperRef}
                style={chartMaxHeight === undefined ? undefined : { maxHeight: chartMaxHeight }}
            >
                {isLoading && <LoadingIndicator />}
                {!isLoading && fromDate && toDate &&
                    <ConnectionHistoryChartComponent
                        assets={assets}
                        fromDate={fromDate}
                        toDate={toDate}
                    />
                }
            </div>

            {/* Pagination — only shown when there are multiple pages */}
            {showPagination && <div className='connection-history__pagination' ref={paginationRef}>
                <GeneralButton
                    className='connection-history__pagination-btn connection-history__pagination-btn--prev'
                    type='normal'
                    text={translate('ABB.Powertrain.Frontend.gatewayManagementPreviousLabel')}
                    icon={IconPreviousPage({ width: '16px', height: '16px' })}
                    onClick={() => { fetchHistory('', prevCursor || ''); setCurrentPage((p) => Math.max(1, p - 1)); }}
                    disabled={!hasPreviousPage}
                    id='connection-history-prev'
                    data-testid='connection-history-prev'
                />

                <div className='connection-history__pagination-info'>
                    <span className='connection-history__pagination-range'>
                        {pageRangeLabel}
                    </span>
                    <div className='connection-history__page-size-group'>
                        <GeneralSelect
                            className='connection-history__page-size-select'
                            options={pageSizeOptions}
                            value={selectedPageSize}
                            onChange={handlePageSizeChange}
                            isSearchable={false}
                            menuPortalTarget={document.body}
                            menuPlacement='top'
                            inputId='connection-history-page-size'
                            name='connection-history-page-size'
                        />
                        <span className='connection-history__assets-per-page'>
                            {translate('ABB.Powertrain.Frontend.gatewayManagementAssetsPerPage')}
                        </span>
                    </div>
                </div>
                <GeneralButton
                    className='connection-history__pagination-btn connection-history__pagination-btn--next'
                    type='normal'
                    text={translate('ABB.Powertrain.Frontend.gatewayManagementNextLabel')}
                    icon={IconNextPage({ width: '16px', height: '16px' })}
                    onClick={() => { fetchHistory(nextCursor || '', ''); setCurrentPage((p) => p + 1); }}
                    disabled={!hasNextPage}
                    id='connection-history-next'
                    data-testid='connection-history-next'
                />
            </div>}
        </div>
    );
};

ConnectedHistoryComponent.propTypes = {
    gatewayID: PropTypes.number,
    onMount: PropTypes.func,
    model: PropTypes.shape({
        data: PropTypes.object,
        loadingStatus: PropTypes.string,
        error: PropTypes.object,
        lastQueryParams: PropTypes.object,
    }),
    actions: PropTypes.shape({
        getConnectedHistory: PropTypes.func,
        clearConnectedHistory: PropTypes.func,
    }),
};

export default ConnectedHistoryComponent;

